import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { Box, Divider, Typography } from "@material-ui/core";
import clsx from "clsx";
import { getConfigTenant, isVesperTenant } from "@capone/common";

import { useCheckoutStateSelector as useSelector } from "@capone/checkout";
import {
  ActionLink,
  AvailabilityDuration,
  AvailabilityLanguage,
  AvailabilityReviewRating,
  ExperienceTag,
  Icon,
  IconName,
  onEnter,
  useDeviceTypes,
} from "halifax";
import { ExperienceBadge } from "redmond";

import styles from "./styles.module.scss";
import { ActivitySelectors } from "../../state/selectors";

import {
  CHANGE_CTA_TEXT,
  DATE,
  OPERATOR,
  START_TIME,
  TOTAL_NUM_TRAVELERS,
  TRAVELERS,
} from "../../../common/textConstants";
import { BadgeExcellenceModal, getTotalTravelersCount } from "../../../common";
import { PATH_SHOP } from "../../../../utils/paths";

interface ActivitySummaryPanelProps {
  displayChangeBtn?: boolean;
}

export const ActivitySummaryPanel = ({
  displayChangeBtn = false,
}: ActivitySummaryPanelProps) => {
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();
  const isVesper = isVesperTenant(getConfigTenant());
  const activityShop = useSelector(ActivitySelectors.getActivityShop);
  const selectedBookableItem = useSelector(ActivitySelectors.getSelectedBookableItem);
  const travelerCounts = useSelector(
    ActivitySelectors.getActivityTravelerCounts
  );
  const priceBreakdown = useSelector(
    ActivitySelectors.getActivityPriceBreakdown
  );
  const [isBadgeExcellenceModalOpen, setBadgeExcellenceModalOpen] =
    useState(false);

  if (!activityShop || !priceBreakdown) return null;

  const {
    rating,
    duration,
    durationFromMinutes,
    durationToMinutes,
    coverImage,
    languageGuides,
    supplierInfo,
    badges,
    name,
  } = activityShop;

  const { date, startTime } = priceBreakdown;

  return (
    <Box
      className={clsx(
        styles["activity-summary-panel-container"],
        { [styles["redesigned"]]: isVesper },
        "activity-summary-panel-container"
      )}
    >
      {!matchesMobile && (
        <Box>
          <img
            src={coverImage}
            alt=""
            className={styles["activity-summary-image"]}
          />
        </Box>
      )}
      <Box className={styles["info-section"]}>
        <Box className={styles["top-section"]}>
          <Box className={styles["activity-info"]}>
            <AvailabilityReviewRating
              reviewCount={rating.numberOfReviews}
              scaledScore={rating.reviewAverage}
              shortReviews
            />
            {duration && (
              <>
                <Divider
                  className={styles["vertical-dividers"]}
                  orientation="vertical"
                />
                <AvailabilityDuration
                  duration={duration}
                  durationFromMinutes={durationFromMinutes}
                  durationToMinutes={durationToMinutes}
                />
              </>
            )}
            {languageGuides && languageGuides.length > 0 && (
              <>
                <Divider
                  className={styles["vertical-dividers"]}
                  orientation="vertical"
                />
                <AvailabilityLanguage languages={languageGuides} />
              </>
            )}
            {supplierInfo && !matchesMobile && (
              <>
                <Divider
                  className={styles["vertical-dividers"]}
                  orientation="vertical"
                />
                <Typography className={styles["activity-name-container"]}>
                  {supplierInfo.name}
                </Typography>
              </>
            )}
          </Box>
          <Typography className={styles["activity-title"]} variant="h3">
            {name}
          </Typography>
          <Typography className={styles["activity-bookable-item"]} variant="subtitle2">
            {selectedBookableItem?.title}
          </Typography>
          {badges && badges.length > 0 && (
            <Box className={styles["activity-tags-container"]}>
              {badges.map((badge) => (
                <ExperienceTag
                  key={badge}
                  badge={badge}
                  onBadgeClick={
                    badge === ExperienceBadge.BadgeOfExcellence
                      ? () => {
                        if (!isBadgeExcellenceModalOpen)
                          setBadgeExcellenceModalOpen(true);
                      }
                      : undefined
                  }
                />
              ))}
            </Box>
          )}
        </Box>
        <Box
          className={clsx(styles["bottom-section"], {
            [styles.mobile]: matchesMobile,
          })}
        >
          <Box className={styles["left-section"]}>
            <Box className={styles["booking-option-item"]}>
              <Icon name={IconName.Calendar} />
              <Box>
                <Box className={styles["option-header"]}>{DATE}</Box>
                <Box className={styles["option-value"]}>
                  {dayjs(date).format("ddd, MMM D")}
                </Box>
              </Box>
            </Box>
            <Divider
              className={styles["vertical-dividers"]}
              orientation="vertical"
            />
            {startTime && (
              <>
                <Box className={styles["booking-option-item"]}>
                  <Icon name={IconName.ClockIcon} />
                  <Box>
                    <Box className={styles["option-header"]}>{START_TIME}</Box>
                    <Box className={styles["option-value"]}>{startTime}</Box>
                  </Box>
                </Box>
                <Divider
                  className={styles["vertical-dividers"]}
                  orientation="vertical"
                />
              </>
            )}
            <Box className={styles["booking-option-item"]}>
              <Icon name={IconName.B2BUser} />
              <Box>
                <Box className={styles["option-header"]}>{TRAVELERS}</Box>
                <Box className={styles["option-value"]}>
                  {TOTAL_NUM_TRAVELERS(getTotalTravelersCount(travelerCounts))}
                </Box>
              </Box>
            </Box>
            {supplierInfo && matchesMobile && (
              <Box className={styles["booking-option-item"]}>
                <Icon name={IconName.OperatorIcon} />
                <Box>
                  <Box className={styles["option-header"]}>{OPERATOR}</Box>
                  <Box className={styles["option-value"]}>
                    {supplierInfo.name}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          {displayChangeBtn && (
            <Box className={styles["right-section"]}>
              <ActionLink
                content={
                  <Typography className={styles["action-link-text"]}>
                    {CHANGE_CTA_TEXT}
                  </Typography>
                }
                label={CHANGE_CTA_TEXT}
                onClick={() =>
                  history.push(`${PATH_SHOP}${history.location.search}`)
                }
                onKeyDown={(e) =>
                  onEnter(e, () =>
                    history.push(`${PATH_SHOP}${history.location.search}`)
                  )
                }
              />
            </Box>
          )}
        </Box>
      </Box>
      <BadgeExcellenceModal
        isOpen={isBadgeExcellenceModalOpen}
        isMobile={matchesMobile}
        onClose={() => {
          setBadgeExcellenceModalOpen(false);
        }}
      />
    </Box>
  );
};
